import React, { useEffect, useState } from "react";
import axios from "axios";
import { Button, Result } from "antd";
import { InlineLoading } from "../shared";
import { bootstrapSession, clearSession, signOut } from "../../utils/auth/sessionUtilities";
import { useSessionUser } from "../../utils/auth/sessionUser";
import { isEnrolmentConfined, loadAuthSession, resetAuthSession, setAnonymous, useAuthSession } from "../../lib/http/session";

type Failure = { message: string } | null;

const DEFAULT_FAILURE = "Please try again, or sign out and sign back in.";

const failureMessage = (error: unknown): string => {
	const message = axios.isAxiosError(error) ? error.response?.data?.message : undefined;
	return typeof message === "string" && message !== "" ? message : DEFAULT_FAILURE;
};

// As the root gate (with children) nothing renders until identity has said who this is and, when signed in, /me has answered.
// Without children (inside a route guard) it is the loading or "couldn't load" screen for a guarded page.
const SessionBootstrap: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
	const { status, info } = useAuthSession();
	const profile = useSessionUser();
	const needsSession = status === "unknown";
	// A confined session cannot read /me (403 mfa_enrolment_required); the guards send it to the security page instead.
	const needsProfile = status === "authenticated" && !profile && !isEnrolmentConfined(info);
	const [failure, setFailure] = useState<Failure>(null);
	const [attempt, setAttempt] = useState(0);

	useEffect(() => {
		if (!needsSession) return;
		let cancelled = false;
		loadAuthSession().catch((error: unknown) => {
			if (!cancelled) setFailure({ message: failureMessage(error) });
		});
		return () => {
			cancelled = true;
		};
	}, [needsSession, attempt]);

	useEffect(() => {
		if (!needsProfile) return;
		let cancelled = false;
		setFailure(null);
		bootstrapSession().catch((error: unknown) => {
			if (cancelled) return;
			const status = axios.isAxiosError(error) ? error.response?.status : undefined;
			console.error("session.bootstrap.failed", { status });
			// A dead session is a sign-out, not an error: the route guards then send the user to login.
			if (status === 401) {
				clearSession();
				setAnonymous("unauthenticated");
				return;
			}
			setFailure({ message: failureMessage(error) });
		});
		return () => {
			cancelled = true;
		};
	}, [needsProfile, attempt]);

	// identity being unreachable ("error") still lets public pages render signed-out; guarded pages show the screen below.
	const blocking = needsSession || needsProfile;
	if (children !== undefined && !blocking) return <>{children}</>;

	if ((failure && blocking) || status === "error") {
		return (
			<Result
				status="error"
				title="We couldn't load your account"
				subTitle={failure?.message ?? DEFAULT_FAILURE}
				extra={[
					<Button
						key="retry"
						type="primary"
						onClick={() => {
							setFailure(null);
							if (status === "error") resetAuthSession();
							setAttempt((n) => n + 1);
						}}
					>
						Try again
					</Button>,
					<Button
						key="signout"
						onClick={() => {
							void signOut().finally(() => window.location.assign("/login"));
						}}
					>
						Sign out
					</Button>,
				]}
			/>
		);
	}

	return (
		<div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
			<InlineLoading message="Loading your account…" />
		</div>
	);
};

export default SessionBootstrap;
